import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FileDetail } from './useFileUpload'
import {
  useTempPresignedUrls,
  useTempFiles,
  useDeleteTempFile,
  useCreateEventWithFiles,
} from './useTempFileUpload'
import { useS3Upload } from './useFileUpload'

export enum FileUploadStatus {
  Available = 'AVAILABLE',
  Pending = 'PENDING',
  Deleted = 'DELETED',
}

export interface Attachment {
  id: string
  name?: string | null | undefined
  downloadUrl?: string | null | undefined
  position?: number | null | undefined
}

export interface FilePreview extends Attachment {
  uploadURL?: string | null
  status: FileUploadStatus
  file?: File
}

/**
 * Custom hook that orchestrates the temporary file upload workflow:
 * 1. Get temporary presigned URLs for uploading files
 * 2. Upload files to storage
 * 3. Manage temporary files (list, delete, etc.)
 * 4. Create an event with temporary files
 */
export const useTempFileUploadWorkflow = () => {
  // Track upload progress for each file
  const [progress, setProgress] = useState<Record<string, number>>({})
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([])
  const queryClient = useQueryClient()

  // Get hooks for each step of the process
  const tempPresignedUrlsMutation = useTempPresignedUrls()
  const s3UploadMutation = useS3Upload()
  const createEventWithFilesMutation = useCreateEventWithFiles()
  const deleteTempFileMutation = useDeleteTempFile()
  const { data: tempFiles = [], isLoading: isLoadingTempFiles } = useTempFiles()

  /**
   * Upload files to temporary storage
   */
  const uploadTempFiles = async (files: File[]): Promise<string[]> => {
    if (!files.length) return []

    setIsUploading(true)
    setProgress({})

    try {
      // 1. Convert Files to FileDetail objects
      const fileDetails: FileDetail[] = files.map((file) => ({
        name: file.name,
        contentType: file.type,
        size: file.size,
      }))

      // 2. Get temporary presigned URLs
      const endpoints = await tempPresignedUrlsMutation.mutateAsync({
        files: fileDetails,
      })

      // 3. Upload files to S3 in parallel
      const uploadPromises = endpoints.map(async (endpoint, index) => {
        const file = files[index]

        try {
          await s3UploadMutation.mutateAsync({
            url: endpoint.uploadUrl,
            file,
            contentType: file.type,
            onProgress: (percent) => {
              // Update progress for this specific file
              setProgress((prev) => ({
                ...prev,
                [file.name]: percent,
              }))
            },
          })

          // Update progress one last time to ensure 100%
          setProgress((prev) => ({
            ...prev,
            [file.name]: 100,
          }))

          return endpoint.id
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error)
          setProgress((prev) => ({
            ...prev,
            [file.name]: -1, // Error state
          }))
          return null
        }
      })

      // 4. Wait for all uploads and filter out failed ones
      const fileIds = (await Promise.all(uploadPromises)).filter(
        Boolean
      ) as string[]
      setUploadedFileIds((prev) => [...prev, ...fileIds])

      // 5. Refresh the list of temporary files
      queryClient.invalidateQueries({ queryKey: ['tempFiles'] })

      return fileIds
    } catch (error) {
      console.error('Error in temp upload workflow:', error)
      throw error
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * Create a new event with temporary files
   */
  const createEventWithTempFiles = async (
    eventData: any,
    selectedFileIds: string[] = uploadedFileIds
  ) => {
    if (!selectedFileIds.length) {
      console.warn('No temporary files selected for the event')
    }

    try {
      // Create the event and associate the files
      const newEvent = await createEventWithFilesMutation.mutateAsync({
        eventData,
        tempFileIds: selectedFileIds,
      })

      // Clear the uploaded file IDs since they're now associated with an event
      setUploadedFileIds([])

      return newEvent
    } catch (error) {
      console.error('Error creating event with files:', error)
      throw error
    }
  }

  /**
   * Delete a temporary file
   */
  const deleteTempFile = async (fileId: string) => {
    try {
      await deleteTempFileMutation.mutateAsync(fileId)

      // Remove the file ID from the list of uploaded files
      setUploadedFileIds((prev) => prev.filter((id) => id !== fileId))
    } catch (error) {
      console.error(`Error deleting temporary file ${fileId}:`, error)
      throw error
    }
  }

  /**
   * Converts temporary files to FilePreview format for the AttachmentList component
   */
  const convertTempFilesToPreviews = (): any[] => {
    return tempFiles.map((file, index) => ({
      id: file.id,
      name: file.name,
      downloadUrl: file.url,
      position: index,
      status: FileUploadStatus.Available,
    }))
  }

  /**
   * Get signed URLs for file uploads - uses the existing temp presigned URLs mutation
   */
  const getSignedUrls = async (
    files: { name: string; contentType: string }[]
  ) => {
    try {
      // Convert files to the expected FileDetail format
      const fileDetails: FileDetail[] = files.map((file) => ({
        name: file.name,
        contentType: file.contentType,
        size: 0, // Size is not required for the signed URL generation
      }))

      // Use the existing mutation to get presigned URLs
      const endpoints = await tempPresignedUrlsMutation.mutateAsync({
        files: fileDetails,
      })

      // Format the results to match the expected output
      const results = endpoints.map((endpoint, i) => ({
        ...endpoint,
        file: files[i],
      }))

      return results
    } catch (error) {
      console.error('Error getting signed URLs:', error)
      throw error
    }
  }

  return {
    // State
    tempFiles,
    isLoadingTempFiles,
    uploadedFileIds,
    progress,
    isUploading,

    // Actions
    uploadTempFiles,
    createEventWithTempFiles,
    deleteTempFile,
    convertTempFilesToPreviews,
    getSignedUrls,

    // Errors
    isError:
      tempPresignedUrlsMutation.isError ||
      s3UploadMutation.isError ||
      createEventWithFilesMutation.isError,
    error:
      tempPresignedUrlsMutation.error ||
      s3UploadMutation.error ||
      createEventWithFilesMutation.error,
  }
}
